import React from 'react';
import { ContactsConfig } from '../types/profile';
import { useLocalization } from '../engine/localization/context';
import { MessageCircle, Phone, Mail, Globe, MapPin } from 'lucide-react';

interface QuickActionsProps {
  contacts: ContactsConfig;
}

export const QuickActions: React.FC<QuickActionsProps> = ({ contacts }) => {
  const { resolve, t } = useLocalization();

  const phone = contacts.phone;
  const whatsapp = contacts.whatsapp || phone;
  const email = contacts.email;
  const website = contacts.website;
  const mapUrl = contacts.mapUrl;

  const greeting = contacts.whatsappGreeting
    ? resolve(contacts.whatsappGreeting)
    : '';
  const encodedGreeting = greeting ? encodeURIComponent(greeting) : '';

  const cleanWaNumber = whatsapp ? whatsapp.replace(/[^0-9]/g, '') : '';
  const waUrl = cleanWaNumber
    ? `https://wa.me/${cleanWaNumber}${encodedGreeting ? `?text=${encodedGreeting}` : ''}`
    : undefined;

  return (
    <section aria-label="Quick Actions" className="grid grid-cols-2 sm:grid-cols-4 gap-2 xs:gap-2.5 sm:gap-3 my-3 sm:my-4">
      {/* WhatsApp Action */}
      {waUrl && (
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 min-h-[52px] sm:min-h-[56px] rounded-xl sm:rounded-2xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400 hover:text-emerald-300 transition-all duration-200 hover:-translate-y-0.5 shadow-sm group"
        >
          <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <span className="text-[11px] sm:text-xs font-bold text-center leading-tight">{t('action.whatsapp')}</span>
        </a>
      )}

      {/* Phone Call Action */}
      {phone && (
        <a
          href={`tel:${phone}`}
          className="flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 min-h-[52px] sm:min-h-[56px] rounded-xl sm:rounded-2xl bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 text-sky-400 hover:text-sky-300 transition-all duration-200 hover:-translate-y-0.5 shadow-sm group"
        >
          <Phone className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <span className="text-[11px] sm:text-xs font-bold text-center leading-tight">{t('action.call')}</span>
        </a>
      )}

      {/* Email Action */}
      {email && (
        <a
          href={`mailto:${email}`}
          className="flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 min-h-[52px] sm:min-h-[56px] rounded-xl sm:rounded-2xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-400 hover:text-amber-300 transition-all duration-200 hover:-translate-y-0.5 shadow-sm group"
        >
          <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <span className="text-[11px] sm:text-xs font-bold text-center leading-tight">{t('action.email')}</span>
        </a>
      )}

      {/* Website Action */}
      {website && (
        <a
          href={website}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 min-h-[52px] sm:min-h-[56px] rounded-xl sm:rounded-2xl bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/30 text-purple-400 hover:text-purple-300 transition-all duration-200 hover:-translate-y-0.5 shadow-sm group"
        >
          <Globe className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <span className="text-[11px] sm:text-xs font-bold text-center leading-tight">{t('action.website')}</span>
        </a>
      )}

      {/* Location Action (if available) */}
      {mapUrl && (
        <a
          href={mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="col-span-2 sm:col-span-4 flex items-center justify-center gap-2 p-2.5 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/60 text-slate-300 hover:text-white transition-colors text-xs font-medium"
        >
          <MapPin className="w-4 h-4 text-sky-400" />
          <span>{t('action.location')}</span>
        </a>
      )}
    </section>
  );
};

